import React, { useState, useEffect, useCallback } from 'react';
import { filter } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Plus, MoreVertical, Search, Trash2, Webhook, Pencil } from 'lucide-react';
// redux
import { useDispatch } from 'src/redux/store';
import useAuth from 'src/hooks/useAuth';
// actions
import actionCriarWebhook from 'src/actions/clientes/actionCriarWebhook';
import actionEditarWebhook from 'src/actions/clientes/actionEditarWebhook';
import actionDeletarWebhook from 'src/actions/clientes/actionDeletarWebhook';
import actionListaWebhooks from 'src/actions/clientes/actionListaWebhooks';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
// utils
import { fDateTime } from 'src/utils/formatTime';
import { getTransformEventsWebhook } from 'src/utils/functions';
// Shadcn UI
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Badge } from 'src/@/components/ui/badge';
import { Label } from 'src/@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'src/@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/@/components/ui/dropdown-menu';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';
import { MultiSelect } from 'src/@/components/ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
    { id: 'nome', label: 'Nome' },
    { id: 'data_criacao', label: 'Data Criacao' },
    { id: 'status', label: 'Status' },
    { id: 'type', label: 'Tipo' },
    { id: 'method', label: 'Metodo' },
    { id: 'acoes', label: 'Acoes', align: 'right' },
];

const TIPO_OPTIONS = [
    { label: 'WhatsApp', value: 'whatsapp' }
];

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    if (query) {
        return filter(array, (_user) => _user.nome.toLowerCase().indexOf(query.toLowerCase()) !== -1);
    }
    return stabilizedThis.map((el) => el[0]);
}

export default function MainWebhooks() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const [page, setPage] = useState(0);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('nome');
    const [filterName, setFilterName] = useState('');
    const [rowsPerPage] = useState(25);
    const [openWebhook, setOpenWebook] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);

    // Form states
    const [nome, setNome] = useState('');
    const [url, setUrl] = useState('');
    const [eventos, setEventos] = useState([]);
    const [tipo, setTipo] = useState('');
    const [canaisSelecionados, setCanaisSelecionados] = useState([]);

    // Edit states
    const [editWebhook, setEditWebhook] = useState(null);
    const [editNome, setEditNome] = useState('');
    const [editUrl, setEditUrl] = useState('');
    const [editEventos, setEditEventos] = useState([]);
    const [editTipo, setEditTipo] = useState('');
    const [editCanais, setEditCanais] = useState([]);

    // Logic states
    const [webhooks, setWebhooks] = useState([]);
    const [canais, setCanais] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const eventsList = [ 'whatsapp.pesquisa_concluida', 'whatsapp.after_resposta_pesquisa', 'wpp_text_client_init', 'whatsapp.message.status'];
    const eventsOptions = eventsList.map((e) => ({
        label: getTransformEventsWebhook(e) || e,
        value: e,
    }));

    const canaisOptions = canais.map((c) => ({
        label: c.nome || c.name || String(c.cliente_id || c._id),
        value: String(c.cliente_id || c._id),
    }));

    const buscarWebhooks = useCallback(async () => {
        try {
            setLoading(true);
            const response = await actionListaWebhooks(user.clienteId);
            setWebhooks(response.data.data);
        } catch (error) {
            console.log('error', error);
        } finally {
            setLoading(false);
        }
    }, [user.clienteId]);

    const buscarCanais = useCallback(async () => {
        try {
            const response = await actionListaCanaisPorCliente(user.clienteId);
            setCanais(response.data.data || response.data || []);
        } catch (error) {
            console.log('error canais', error);
        }
    }, [user.clienteId]);

    useEffect(() => {
        buscarWebhooks();
        buscarCanais();
    }, [buscarWebhooks, buscarCanais]);

    async function onCriarWebhook() {
        try {
            if (!nome || !url) {
                enqueueSnackbar('Preencha os campos obrigatorios', { variant: 'error' });
                return;
            }
            setSaving(true);
            let data = {
                nome,
                url,
                method: 'POST',
                events: eventos,
                type: tipo,
                cliente_ids: canaisSelecionados,
                clienteId: user.clienteId,
            };
            await actionCriarWebhook(data);
            setOpenWebook(false);
            resetForm();
            buscarWebhooks();
            enqueueSnackbar('Webhook criado com sucesso!', { variant: 'success' });
        } catch (error) {
            console.log('error', error);
            enqueueSnackbar('Erro ao criar webhook', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    }

    async function onEditarWebhook() {
        try {
            if (!editNome || !editUrl) {
                enqueueSnackbar('Preencha os campos obrigatorios', { variant: 'error' });
                return;
            }
            setSaving(true);
            let data = {
                nome: editNome,
                url: editUrl,
                events: editEventos,
                type: editTipo,
                cliente_ids: editCanais,
            };
            await actionEditarWebhook(editWebhook._id, data);
            setOpenEdit(false);
            setEditWebhook(null);
            buscarWebhooks();
            enqueueSnackbar('Webhook atualizado com sucesso!', { variant: 'success' });
        } catch (error) {
            console.log('error', error);
            enqueueSnackbar('Erro ao editar webhook', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    }

    async function onDeletarWebhook(webhookId) {
        try {
            await actionDeletarWebhook(webhookId);
            buscarWebhooks();
            enqueueSnackbar('Webhook deletado com sucesso!', { variant: 'success' });
        } catch (error) {
            console.log('error', error);
            enqueueSnackbar('Erro ao deletar webhook', { variant: 'error' });
        }
    }

    function handleOpenEdit(row) {
        setEditWebhook(row);
        setEditNome(row.nome || '');
        setEditUrl(row.url || '');
        setEditEventos(row.events || []);
        setEditTipo(row.type || '');
        setEditCanais(row.cliente_ids || []);
        setOpenEdit(true);
    }

    const resetForm = () => {
        setNome('');
        setUrl('');
        setEventos([]);
        setTipo('');
        setCanaisSelecionados([]);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const filteredWebhooks = applySortFilter(webhooks, getComparator(order, orderBy), filterName);
    const isNotFound = filteredWebhooks.length === 0;

    const getTipoLabel = (type) => {
        const found = TIPO_OPTIONS.find((t) => t.value === type);
        return found ? found.label : type || '-';
    };

    // Shared form fields component
    const WebhookFormFields = ({ formNome, setFormNome, formUrl, setFormUrl, formTipo, setFormTipo, formEventos, setFormEventos, formCanais, setFormCanais }) => (
        <div className="p-6 flex flex-col gap-5 bg-[#F9FAFB]">
            <div className="grid gap-2">
                <Label htmlFor="form-nome" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Nome
                </Label>
                <Input id="form-nome" value={formNome} onChange={(e) => setFormNome(e.target.value)} placeholder="Ex: Integracao CRM" className="bg-white" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 grid gap-2">
                    <Label htmlFor="form-url" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        URL
                    </Label>
                    <Input
                        id="form-url"
                        value={formUrl}
                        onChange={(e) => setFormUrl(e.target.value)}
                        placeholder="https://api.seusistema.com/webhook"
                        className="bg-white"
                    />
                </div>

                <div className="grid gap-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Metodo</Label>
                    <RadioGroup defaultValue="POST" className="flex items-center h-10">
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="POST" id="r-post" />
                            <Label htmlFor="r-post" className="font-normal cursor-pointer">
                                POST
                            </Label>
                        </div>
                    </RadioGroup>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tipo</Label>
                    <Select value={formTipo} onValueChange={setFormTipo}>
                        <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                            {TIPO_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid gap-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Canais</Label>
                    <MultiSelect
                        options={canaisOptions}
                        onValueChange={setFormCanais}
                        defaultValue={formCanais}
                        placeholder="Selecione os canais"
                        variant="secondary"
                        maxCount={3}
                        className="bg-white"
                    />
                </div>
            </div>

            <div className="grid gap-2">
                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Eventos</Label>
                <MultiSelect
                    options={eventsOptions}
                    onValueChange={setFormEventos}
                    defaultValue={formEventos}
                    placeholder="Selecione os eventos"
                    variant="secondary"
                    maxCount={3}
                    className="bg-white"
                />
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full w-full gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Action Button */}
            <div className="flex justify-end">
                <Button
                    onClick={() => setOpenWebook(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-[#7C3AED] hover:bg-[#6d28d9] text-white rounded-xl shadow-sm shadow-[#7C3AED]/20"
                >
                    <Plus size={18} />
                    <span>Criar Webhook</span>
                </Button>
            </div>

            {/* Dialog Criar Webhook */}
            <Dialog open={openWebhook} onOpenChange={setOpenWebook}>
                <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden border-none rounded-xl shadow-2xl">
                    <div className="p-6 pb-2 border-b border-gray-100 bg-white">
                        <DialogTitle className="text-xl font-bold text-gray-900">Novo Webhook</DialogTitle>
                        <DialogDescription>Configure os detalhes do seu novo webhook para receber eventos.</DialogDescription>
                    </div>

                    <WebhookFormFields
                        formNome={nome}
                        setFormNome={setNome}
                        formUrl={url}
                        setFormUrl={setUrl}
                        formTipo={tipo}
                        setFormTipo={setTipo}
                        formEventos={eventos}
                        setFormEventos={setEventos}
                        formCanais={canaisSelecionados}
                        setFormCanais={setCanaisSelecionados}
                    />

                    <div className="p-6 pt-2 bg-white flex justify-end gap-2 border-t border-gray-100">
                        <Button variant="ghost" onClick={() => setOpenWebook(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={onCriarWebhook} disabled={saving} className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white">
                            {saving ? 'Salvando...' : 'Salvar Webhook'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog Editar Webhook */}
            <Dialog open={openEdit} onOpenChange={setOpenEdit}>
                <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden border-none rounded-xl shadow-2xl">
                    <div className="p-6 pb-2 border-b border-gray-100 bg-white">
                        <DialogTitle className="text-xl font-bold text-gray-900">Editar Webhook</DialogTitle>
                        <DialogDescription>Atualize os detalhes do webhook.</DialogDescription>
                    </div>

                    <WebhookFormFields
                        formNome={editNome}
                        setFormNome={setEditNome}
                        formUrl={editUrl}
                        setFormUrl={setEditUrl}
                        formTipo={editTipo}
                        setFormTipo={setEditTipo}
                        formEventos={editEventos}
                        setFormEventos={setEditEventos}
                        formCanais={editCanais}
                        setFormCanais={setEditCanais}
                    />

                    <div className="p-6 pt-2 bg-white flex justify-end gap-2 border-t border-gray-100">
                        <Button variant="ghost" onClick={() => setOpenEdit(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={onEditarWebhook} disabled={saving} className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white">
                            {saving ? 'Atualizando...' : 'Atualizar Webhook'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Content */}
            <div className="flex flex-col gap-4">
                {/* Search / Toolbar */}
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100">
                    <div className="relative w-full max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Pesquisar webhooks..."
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                            className="pl-9 bg-gray-50 border-gray-200 rounded-lg"
                        />
                    </div>
                    <div className="text-sm text-gray-500">{filteredWebhooks.length} registros encontrados</div>
                </div>

                {/* Table */}
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <Table>
                        <TableHeader className="bg-gray-50">
                            <TableRow>
                                {TABLE_HEAD.map((headCell) => (
                                    <TableHead
                                        key={headCell.id}
                                        className={`text-xs font-bold uppercase tracking-wider text-gray-500 ${headCell.align === 'right' ? 'text-right' : ''}`}
                                        onClick={() => handleRequestSort(headCell.id)}
                                    >
                                        <span className="cursor-pointer hover:text-gray-700 flex items-center gap-1">{headCell.label}</span>
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredWebhooks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row._id} className="hover:bg-gray-50 transition-colors">
                                    <TableCell className="font-medium text-gray-900">{row.nome}</TableCell>
                                    <TableCell className="text-gray-500">{fDateTime(row.data_criacao)}</TableCell>
                                    <TableCell>
                                        <Badge variant={row.status === false ? 'destructive' : 'green'} className="rounded-md">
                                            {row.status === false ? 'Inativo' : 'Ativo'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="rounded-md text-xs">
                                            {getTipoLabel(row.type)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-gray-500 font-mono text-xs">{row.method}</TableCell>
                                    <TableCell className="text-right">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" className="h-8 w-8 p-0">
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-[160px] rounded-xl">
                                                <DropdownMenuItem
                                                    className="cursor-pointer"
                                                    onClick={() => handleOpenEdit(row)}
                                                >
                                                    <Pencil className="mr-2 h-4 w-4" />
                                                    Editar
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="text-red-600 focus:text-red-600 cursor-pointer"
                                                    onClick={() => onDeletarWebhook(row._id)}
                                                >
                                                    <Trash2 className="mr-2 h-4 w-4" />
                                                    Deletar
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {isNotFound && (
                                <TableRow>
                                    <TableCell colSpan={6} className="h-24 text-center">
                                        <div className="flex flex-col items-center justify-center p-8 text-center text-gray-500">
                                            <Webhook className="h-12 w-12 mb-4 text-gray-300" />
                                            <p className="text-lg font-medium text-gray-900">Nenhum webhook encontrado</p>
                                            <p className="text-sm">Tente ajustar os filtros ou crie um novo webhook.</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    {/* Pagination */}
                    <div className="flex items-center justify-end space-x-2 py-4 px-4 border-t border-gray-100">
                        <span className="text-sm text-gray-500">
                            Pagina {page + 1} de {Math.max(1, Math.ceil(webhooks.length / rowsPerPage))}
                        </span>
                        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                            Anterior
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * rowsPerPage >= webhooks.length}>
                            Proximo
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
